import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import api, { API, euro, apiError, fmtDate, dupDetail, pctIt } from "@/lib/api";
import { BarraElenco, Casella, esportaCsv, useSelezione } from "@/components/SelezioneFile";
import { TipologiaSelect, CatastoSelect, ClassFiltri, passaClass, tipoCatasto } from "@/lib/catalogo";
import { SectionTitle, TempBadge, StatusBadge, DuplicateWarning, Progress, PasswordInput } from "@/components/common";
import { useMarketingMap, MarketingBadge, ExportListaButton } from "@/components/GdprContactPanel";
import { toast } from "sonner";
import { Plus, Flame, ArrowRight, X, Phone, Mail, Lock, Sparkles, CheckCircle2, Webhook, Copy, Check, Download, ShieldCheck, MoreHorizontal } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useDupCheck } from "@/lib/useDupCheck";
import { LongTextarea } from "@/components/LongText";
import { useUnsavedGuard, UnsavedActions, salvatoOk } from "@/components/UnsavedBar";
import { UltimoSalvataggio } from "@/components/SalvataggioUI";
import { EuroInput } from "@/components/EuroInput";
import { DuplicatiIndirizzo } from "@/components/QuickFab";
import { AzioniNotizia, ArchivioView } from "@/components/NotiziaArchivio";
import { valutatoK } from "@/components/ValutazioneImmobilart";
import { useSticky } from "@/lib/sticky";
import { GiraAField, SegnalataDa, SegnalateDaMe, useSchedaSegnalate } from "@/components/GiraNotizia";
import { NotizieTemperature, tempKey } from "@/components/NotizieTemperature";
import { ConvertiIncaricoModal } from "@/components/ConvertiIncarico";
import { UltimoContattoBadge, HoChiamato, DaRichiamare, daRichiamare, perContatto } from "@/components/UltimoContatto";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";

const STATI = ["nuova","da_contattare","contattata","follow_up","appuntamento","valutazione","trattativa","incarico_acquisito","persa","richiamare"];
const FONTE_LABEL = { sito: "Sito Web", portale: "Portale", meta: "Meta / Social", generico: "Integrazione" };
export const PROVENIENZE = ["Passaparola/Referenza", "Conoscenza personale", "Marketing offline/online", "Eventi", "Clienti passati", "Segnalatori", "VDP", "Ex incarico agenzia", "Zona"];
const NOTIZIE_EXPORT_COLS = [["nome","Nome"],["cognome","Cognome"],["telefono","Telefono"],["email","Email"],["via_immobile","Via"],["zona","Zona"],["comune","Comune"],["tipologia","Tipologia"],["categoria_catastale","Categoria catastale"],["provenienza","Provenienza"],["temperatura","Temperatura"],["stato","Stato"],["valore_stimato","Valore stimato"],["created_at","Creata il"]];
function downloadBlob(content, type, filename) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function Notizie() {
  const { monitoraggio: isDirezione, vistaMia, isDirettore, isAgente, user, isSegreteria, isDirezione: isSocio } = useAuth();
  const S = useSelezione();
  const perAgente = isDirezione || isDirettore;
  const [tempOpen, setTempOpen] = useState(null);
  const [caroselloEl, setCaroselloEl] = useState(null);
  const [altro, setAltro] = useState(false);
  const [agente, setAgente] = useSticky("notizie_agente", "all");
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [provFilter, setProvFilter] = useSticky("notizie_prov", "all");
  const [storicoQ, setStoricoQ] = useSticky("notizie_storicoQ", "");
  const [dirQ, setDirQ] = useSticky("notizie_dirQ", "");
  const [fTip, setFTip] = useSticky("notizie_tip", "");
  const [fCat, setFCat] = useSticky("notizie_cat", "");
  const [storicoFrom, setStoricoFrom] = useSticky("notizie_from", "");
  const [storicoTo, setStoricoTo] = useSticky("notizie_to", "");
  const [esitoFilter, setEsitoFilter] = useSticky("notizie_esito", "all");
  const [view, setView] = useSticky("notizie_view", "attive");
  const [mieFiltro, setMieFiltro] = useSticky("notizie_mie_filtro", "tutte");
  const [mieAg, setMieAg] = useState("all");
  useSchedaSegnalate(setView);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if ((isAgente || vistaMia) && !new URLSearchParams(window.location.search).get("scheda")) setView("mie"); }, [isAgente, vistaMia]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (isDirezione && view === "mie") setView("attive"); }, [isDirezione]);
  const [modal, setModal] = useState(false);
  const [webhook, setWebhook] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmN, setConfirmN] = useState(null);
  const [leadStats, setLeadStats] = useState(null);
  const [leadTrend, setLeadTrend] = useState([]);
  const [showReport, setShowReport] = useState(false);

  const storico = view === "storico";
  const [soloMkt, setSoloMkt] = useState(false);
  const mktMap = useMarketingMap("notizia");
  const arch = view === "archiviate" || view === "cestino" || view === "segnalate";
  const reqRef = useRef(0);
  const load = () => { if (arch) { setLoading(false); return Promise.resolve(); } const my = ++reqRef.current; return api.get(storico ? "/notizie?archiviate=true" : view === "mie" ? "/notizie?girate=true" : "/notizie").then((r) => { if (my !== reqRef.current) return; setItems(r.data); setLoading(false); }); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLoading(true); load(); }, [view]);
  useEffect(() => {
    api.get("/lead-stats").then((r) => setLeadStats(r.data)).catch(() => {});
    api.get("/lead-stats/storico").then((r) => setLeadTrend(r.data)).catch(() => {});
  }, []);

  const sq = storicoQ.trim().toLowerCase();
  const dq = dirQ.trim().toLowerCase();
  const matchSP = (n) => {
    if (dq && !`${n.nome || ""} ${n.cognome || ""} ${n.via_immobile || ""} ${n.indirizzo || ""} ${n.telefono || ""} ${n.owner_agent_name || ""}`.toLowerCase().includes(dq)) return false;
    return (provFilter === "all" || n.provenienza === provFilter) && passaClass(n, fTip, fCat);
  };
  const agenti = Object.entries(items.reduce((a, n) => { if (n.owner_agent_id) a[n.owner_agent_id] = n.owner_agent_name || n.agente_referente || "—"; return a; }, {})).sort((a, b) => a[1].localeCompare(b[1]));
  const mie = view === "mie";
  const passaMie = (n) => (n.owner_agent_id === user?.id || n._girata) && !(mieFiltro === "gestite" && n._girata) && !(mieFiltro === "girate" && !n._girata);
  const mieScoped = mie ? items.filter((n) => matchSP(n) && passaMie(n)) : [];
  const mieAgenti = [...(mieScoped.some((n) => !n._girata) ? [[user?.id, "Gestite da me"]] : []),
    ...Object.entries(mieScoped.reduce((a, n) => { if (n._girata) a[n.owner_agent_id] = `Girate a ${n.owner_agent_name || "—"}`; return a; }, {})).sort((a, b) => a[1].localeCompare(b[1]))];
  const mieAgId = mieAgenti.some(([id]) => id === mieAg) ? mieAg : "all";
  const filtered = items.filter((n) => {
    if (storico) {
      const hay = `${n.nome || ""} ${n.cognome || ""} ${n.via_immobile || ""} ${n.immobile || ""} ${n.provenienza || ""}`.toLowerCase();
      if (sq && !hay.includes(sq)) return false;
      const d = (n.updated_at || n.created_at || "").slice(0, 10);
      if (storicoFrom && d && d < storicoFrom) return false;
      if (storicoTo && d && d > storicoTo) return false;
      if (esitoFilter !== "all" && (n.chiamata_esito || "") !== esitoFilter) return false;
      return true;
    }
    if (!matchSP(n)) return false;
    if (mie) return passaMie(n) && (mieAgId === "all" || n.owner_agent_id === mieAgId);
    if (n._girata) return false;
    return !(perAgente && agente !== "all" && n.owner_agent_id !== agente);
  });
  const listaChiusa = !storico && !tempOpen && !dq && provFilter === "all" && !fTip && !fCat;
  const carosello = !storico && !!tempOpen;

  const convAt = (n) => {
    const ev = [...(n.timeline || [])].reverse().find((e) => e.tipo === "incarico_acquisito");
    return ev?.at || n.updated_at || n.created_at || "";
  };
  const display0 = storico ? [...filtered].sort((a, b) => (convAt(b) || "").localeCompare(convAt(a) || ""))
    : listaChiusa || tempOpen === "richiamare" ? [] : filtered.filter((n) => !tempOpen || tempOpen === "tutte" || tempKey(n) === tempOpen).sort(perContatto);
  const baseRichiamo = mie ? mieScoped.filter((n) => !n._girata) : items.filter((n) => matchSP(n) && !n._girata && !n._masked);
  const nRichiamo = daRichiamare(baseRichiamo, 90).length;
  const display = soloMkt ? display0.filter((n) => mktMap && mktMap[n.id]) : display0;
  const provCounts = items.reduce((a, n) => { const p = n.provenienza; if (p) a[p] = (a[p] || 0) + 1; return a; }, {});

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = NOTIZIE_EXPORT_COLS.map((c) => esc(c[1])).join(",");
    const rows = display.map((n) => NOTIZIE_EXPORT_COLS.map((c) => esc(n[c[0]])).join(","));
    const tag = `${provFilter !== "all" ? `_${provFilter}` : ""}${storico ? "_storico" : ""}`;
    downloadBlob("\ufeff" + [header, ...rows].join("\n"), "text/csv;charset=utf-8;", `notizie${tag}.csv`);
  };
  const exportXls = () => {
    const h = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const head = NOTIZIE_EXPORT_COLS.map((c) => `<th>${h(c[1])}</th>`).join("");
    const body = display.map((n) => `<tr>${NOTIZIE_EXPORT_COLS.map((c) => `<td>${h(n[c[0]])}</td>`).join("")}</tr>`).join("");
    const html = `<html><head><meta charset="utf-8"></head><body><table border="1"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`;
    const tag = `${provFilter !== "all" ? `_${provFilter}` : ""}${storico ? "_storico" : ""}`;
    downloadBlob("\ufeff" + html, "application/vnd.ms-excel", `notizie${tag}.xls`);
  };

  const azioni = (
    <>
      {isDirezione && (
        <button data-testid="webhook-btn" onClick={() => setWebhook(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-[var(--border)] text-slate-700 font-semibold text-sm hover:border-[#FC82ED]/40 transition-all">
          <Webhook size={16} /> Integrazione Lead
        </button>
      )}
      <button data-testid="report-chiamate-btn" onClick={() => setShowReport(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-[var(--border)] text-slate-700 font-semibold text-sm hover:border-[#FC82ED]/40 transition-all">
        <Phone size={16} /> Report chiamate
      </button>
      <button data-testid="export-csv-btn" onClick={exportCsv}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-[var(--border)] text-slate-700 font-semibold text-sm hover:border-[#FC82ED]/40 transition-all">
        <Download size={16} /> Esporta CSV
      </button>
      <button data-testid="export-xls-btn" onClick={exportXls}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-[var(--border)] text-slate-700 font-semibold text-sm hover:border-[#FC82ED]/40 transition-all">
        <Download size={16} /> Esporta Excel
      </button>
      <button data-testid="filter-marketing-notizie" onClick={() => setSoloMkt((v) => !v)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border font-semibold text-sm transition-all ${soloMkt ? "bg-emerald-500 text-white border-emerald-500" : "bg-white border-[var(--border)] text-slate-700 hover:border-[#FC82ED]/40"}`}>
        <ShieldCheck size={16} /> Solo consenso marketing
      </button>
      <ExportListaButton contactType="notizia" />
    </>
  );

  return (
    <div className="fade-up">
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="min-w-0"><SectionTitle sub="Opportunità di acquisizione classificate per temperatura.">Notizie Acquisizione</SectionTitle></div>
        <button data-testid="new-notizia-btn" onClick={() => setModal(true)}
                className="shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm transition-all glow-pink whitespace-nowrap">
          <Plus size={16} /> Nuova notizia
        </button>
      </div>
      <div className="flex items-center justify-between mb-5 gap-2 flex-wrap">
        <div className="flex flex-wrap gap-1 bg-[#F7F7F8] border border-[var(--border)] rounded-xl p-1 max-w-full">
            <button data-testid="view-mie" onClick={() => setView("mie")} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${view === "mie" ? "bg-[#FC82ED] text-black" : "text-slate-600 hover:text-[#111111]"}`}>Le mie</button>
            <button data-testid="view-attive" onClick={() => setView("attive")} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${view === "attive" ? "bg-[#FC82ED] text-black" : "text-slate-600 hover:text-[#111111]"}`}>Attive</button>
            <button data-testid="view-storico" onClick={() => setView("storico")} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${storico ? "bg-[#FC82ED] text-black" : "text-slate-600 hover:text-[#111111]"}`}>Storico</button>
            <button data-testid="view-archiviate" onClick={() => setView("archiviate")} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${view === "archiviate" ? "bg-[#FC82ED] text-black" : "text-slate-600 hover:text-[#111111]"}`}>Archiviate</button>
            <button data-testid="view-cestino" onClick={() => setView("cestino")} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${view === "cestino" ? "bg-[#FC82ED] text-black" : "text-slate-600 hover:text-[#111111]"}`}>Cestino</button>
            <button data-testid="view-segnalate" onClick={() => setView("segnalate")} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${view === "segnalate" ? "bg-[#FC82ED] text-black" : "text-slate-600 hover:text-[#111111]"}`}>Segnalate da me</button>
        </div>
        <div className="relative ml-auto">
          <button data-testid="notizie-altro-btn" aria-expanded={altro} onClick={() => setAltro((v) => !v)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl border font-semibold text-sm ${altro ? "bg-[#111111] text-white border-[#111111]" : "bg-white border-[var(--border)] text-slate-700"}`}>
            Altro <MoreHorizontal size={16} />
          </button>
          {altro && <div className="fixed inset-0 z-20" onClick={() => setAltro(false)} />}
          {altro && (
            <div data-testid="notizie-altro-menu" className="absolute right-0 top-full mt-2 z-30 w-64 p-2 rounded-2xl bg-white border border-[var(--border)] shadow-xl flex flex-col gap-2 [&>*]:w-full">
              {azioni}
            </div>
          )}
        </div>
      </div>

      {leadStats && leadStats.totale > 0 && (
        <div data-testid="lead-stats" className="card-immo p-4 mb-4">
          <div className="flex items-center gap-2 text-sm font-semibold mb-3">
            <Webhook size={15} className="text-[#FC82ED]" /> Sorgenti Lead · Conversione per canale
            <span className="text-xs text-[var(--muted)] ml-auto">Totale <b className="text-[#111111]">{leadStats.totale}</b> · Convertiti <b className="text-emerald-500">{leadStats.convertiti}</b> · <b className="text-[#FC82ED]">{pctIt(leadStats.conversione)}</b></span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.entries(leadStats.per_fonte).map(([k, v]) => (
              <div key={k} data-testid={`lead-stat-${k}`} className="rounded-xl border border-[var(--border)] p-3 bg-slate-50/60">
                <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)]">{FONTE_LABEL[k]}</div>
                <div className="flex items-end justify-between mt-1"><span className="mono font-extrabold text-lg">{v.ricevuti}</span><span className="text-xs text-[var(--muted)]">lead</span></div>
                <div className="flex justify-between text-[11px] mt-1"><span className="text-emerald-600">{v.convertiti} conv.</span><span className="text-[#FC82ED] font-semibold">{pctIt(v.conversione)}</span></div>
                <Progress value={v.conversione} className="mt-1.5" />
                {v.costo_per_lead > 0 && <div data-testid={`cpl-${k}`} className="text-[10px] text-[var(--muted)] mt-1">€{v.costo_per_lead}/lead</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {leadTrend.some((m) => (m.sito + m.portale + m.meta + m.generico) > 0) && (
        <div data-testid="lead-chart" className="card-immo p-4 mb-4">
          <div className="flex items-center gap-2 text-sm font-semibold mb-3"><Webhook size={15} className="text-[#FC82ED]" /> Storico lead per canale · ultimi 6 mesi</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={leadTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ECECEF" vertical={false} />
              <XAxis dataKey="mese" stroke="#6B7280" fontSize={11} />
              <YAxis stroke="#6B7280" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #ECECEF", borderRadius: 12 }} />
              <Legend />
              <Bar dataKey="sito" name="Sito" fill="#FC82ED" radius={[4, 4, 0, 0]} />
              <Bar dataKey="portale" name="Portale" fill="#38BDF8" radius={[4, 4, 0, 0]} />
              <Bar dataKey="meta" name="Meta" fill="#FFB020" radius={[4, 4, 0, 0]} />
              <Bar dataKey="generico" name="Integrazione" fill="#94A3B8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {!storico && (
        <div className="space-y-2 mb-6">
          {mie && <div className="flex gap-1.5 flex-wrap items-center" data-testid="mie-filtri">
            {[["tutte", "Tutte"], ["gestite", "Gestite da me"], ["girate", "Girate da me"]].map(([k, l]) => (
              <button key={k} data-testid={`mie-filtro-${k}`} onClick={() => { setMieFiltro(k); setMieAg("all"); }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${mieFiltro === k ? "bg-[#111111] text-white border-[#111111]" : "border-[var(--border)] text-slate-600 hover:border-[#FC82ED]/40"}`}>{l}</button>
            ))}
          </div>}
          {!arch && (mie
            ? <NotizieTemperature items={filtered} scoped={mieScoped} open={tempOpen} setOpen={setTempOpen} agente={mieAgId} setAgente={setMieAg} agenti={mieAgenti} showMenu={false} showTable slotRef={setCaroselloEl} richiamare={nRichiamo} />
            : <NotizieTemperature items={filtered} scoped={items.filter((n) => matchSP(n) && !n._girata)} open={tempOpen} setOpen={setTempOpen} agente={agente} setAgente={setAgente}
            agenti={agenti} showMenu={perAgente} showTable={isDirezione} slotRef={setCaroselloEl} richiamare={nRichiamo} />)}
          {!arch && tempOpen === "richiamare" && <DaRichiamare items={baseRichiamo} agenti={agenti} conFiltroAgente={!mie && (perAgente || isDirezione)} onDone={load} />}
          {!arch && <ClassFiltri tip={fTip} setTip={setFTip} cat={fCat} setCat={setFCat} testid="notizie-filtri-class" />}
          {view !== "segnalate" && <div className="flex gap-2 flex-wrap items-center">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mr-1">Provenienza</span>
            {[["all","Tutte"], ...PROVENIENZE.map((p) => [p, p])].map(([k,l]) => {
              const cnt = k === "all" ? items.length : (provCounts[k] || 0);
              return (
              <button key={k} data-testid={`prov-filter-${k}`} onClick={() => setProvFilter(k)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${provFilter===k ? "bg-[#FC82ED] text-black border-[#FC82ED]" : "border-[var(--border)] text-slate-600 hover:border-[#FC82ED]/40"}`}>
                {l} <span data-testid={`prov-count-${k}`} className={`ml-1 ${provFilter===k ? "text-black/60" : "text-[var(--muted)]"}`}>{cnt}</span>
              </button>
              );
            })}
          </div>}
        </div>
      )}
      {isDirezione && !storico && (
        <input data-testid="direzione-notizie-search" value={dirQ} onChange={(e) => setDirQ(e.target.value)} placeholder="Cerca in tutte le notizie (nome, via, telefono, agente)…"
               className="w-full mb-4 px-3 py-2.5 rounded-xl bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-base sm:text-sm" />
      )}
      {storico && (
        <div className="flex gap-2 mb-6 flex-wrap items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1">Cerca (nome, via, provenienza)</label>
            <input data-testid="storico-search" value={storicoQ} onChange={(e) => setStoricoQ(e.target.value)} placeholder="Cerca nello storico…"
                   className="w-full px-3 py-2 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1">Dal</label>
            <input data-testid="storico-from" type="date" value={storicoFrom} onChange={(e) => setStoricoFrom(e.target.value)}
                   className="px-3 py-2 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1">Al</label>
            <input data-testid="storico-to" type="date" value={storicoTo} onChange={(e) => setStoricoTo(e.target.value)}
                   className="px-3 py-2 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm" />
          </div>
          {(storicoQ || storicoFrom || storicoTo) && (
            <button data-testid="storico-clear" onClick={() => { setStoricoQ(""); setStoricoFrom(""); setStoricoTo(""); }}
                    className="px-3 py-2 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-xs font-medium hover:bg-slate-100">Azzera</button>
          )}
          <div className="flex items-center gap-1.5 basis-full flex-wrap">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)]">Esito chiamata</span>
            {[["all","Tutti"],["fatta","Fatta"],["non_risponde","Non risponde"],["da_rifare","Da rifare"]].map(([k,l]) => (
              <button key={k} data-testid={`esito-filter-${k}`} onClick={() => setEsitoFilter(k)}
                      className={`px-2.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${esitoFilter===k ? "bg-[#FC82ED] text-black border-[#FC82ED]" : "border-[var(--border)] text-slate-600 hover:border-[#FC82ED]/40"}`}>{l}</button>
            ))}
          </div>
        </div>
      )}

      {!loading && (storico || provFilter !== "all") && (
        <div className="text-xs text-[var(--muted)] mb-3" data-testid="notizie-count">
          {display.length} {display.length === 1 ? "risultato" : "risultati"}{storico ? " · ordinati per data di trasformazione (più recenti)" : ""}
        </div>
      )}
      {view === "segnalate" ? <SegnalateDaMe /> : arch ? <ArchivioView kind={view} q={isDirezione ? dirQ : ""} /> : loading ? <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">{[...Array(3)].map((_,i)=><div key={i} className="skeleton h-56 rounded-2xl"/>)}</div> :
      listaChiusa || tempOpen === "richiamare" ? (tempOpen === "richiamare" ? null : <div className="text-sm text-[var(--muted)] py-4" data-testid="notizie-chiuse-hint">Apri un riquadro per vedere l'elenco delle notizie.</div>) :
      display.length === 0 ? (!storico && (tempOpen || provFilter !== "all" || dq)
        ? <div className="card-immo p-10 text-center" data-testid="notizie-filtri-vuoto"><p className="text-[var(--muted)] text-sm mb-3">Nessuna notizia con questi filtri</p>
            <button data-testid="notizie-azzera-filtri" onClick={() => { setProvFilter("all"); setDirQ(""); setAgente("all"); }} className="px-4 py-2 rounded-xl bg-[#111111] text-white text-sm font-semibold">Azzera filtri</button></div>
        : <Empty storico={storico} />) :
      (() => { const elencoNode = (
      <div id="notizie-elenco" className={carosello ? "flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 scroll-mt-20 [&>*:not(.sel-tutto)]:w-[300px] sm:[&>*:not(.sel-tutto)]:w-[340px] [&>*]:shrink-0 [&>*:not(.sel-tutto)]:snap-start" : "grid md:grid-cols-2 xl:grid-cols-3 gap-4 scroll-mt-20"}>
      <BarraElenco sel={S.sel} onAnnulla={S.annulla} onFatto={load}
        azioni={[{ k: "archivia", label: "Archivia", conferma: (n) => `Archiviare ${n} notizie?`, esito: "archiviate", run: (id) => api.post(`/notizie/${id}/archivia`, { motivo: "altro", nota: "Archiviata in blocco" }) }]}
        cambiaAgente={isSocio || isSegreteria ? (id, ag) => api.put(`/notizie/${id}`, { owner_agent_id: ag }) : null}
        onEsporta={(ids) => esportaCsv("Notizie", items.filter((x) => ids.includes(x.id)), [["titolo", "Titolo"], ["indirizzo", "Indirizzo"], ["zona", "Zona"], ["stato", "Stato"], ["owner_agent_name", "Agente"], ["created_at", "Creata il"]])} />
      {display.length > 0 && !storico && <div className={carosello ? "sel-tutto self-start pt-1" : "col-span-full -mb-2"}>
        <button data-testid="notizie-seltutto" onClick={() => S.tutti(display.map((x) => x.id))} className="text-xs font-semibold text-slate-600 hover:text-[#111111]">Seleziona tutto ({display.length})</button></div>}
        {display.map((n) => (
          <div key={n.id} data-testid={`notizia-${n.id}`} onClick={() => (S.sel.size ? S.toggle(n.id) : nav(`/notizie/${n.id}`))} className={`relative card-immo overflow-hidden flex flex-col cursor-pointer hover:-translate-y-0.5 hover:border-[#FC82ED]/40 transition-all ${S.sel.has(n.id) ? "ring-2 ring-[#FC82ED]" : ""}`}>
            {!storico && <Casella on={S.sel.has(n.id)} onToggle={() => S.toggle(n.id)} testid={`notizia-sel-${n.id}`} />}
            {n.cover_file_id && (
              <div className="h-36 overflow-hidden">
                <img src={`${API}/files/${n.cover_file_id}/download?thumb=1`} loading="lazy" alt="" data-testid={`notizia-cover-${n.id}`} className="w-full h-full object-cover" />
              </div>
            )}
            <div className={`p-5 flex flex-col gap-3 flex-1 ${!storico && !n.cover_file_id ? "pl-11" : ""}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-base">{n._masked ? n.nome_display : ((n.nome || n.cognome) ? `${n.nome || ""} ${n.cognome || ""}`.trim() : ([n.via_immobile || n.indirizzo, n.comune].filter(Boolean).join(", ") || "Notizia"))}</div>
                <div data-testid={`notizia-agente-${n.id}`} className="text-sm font-semibold text-[#111111] mt-0.5">Agente: <span className="underline decoration-[#FC82ED] decoration-2 underline-offset-2">{n.owner_agent_name || n.agente_referente || "—"}</span></div>
                <SegnalataDa n={n} testid={`notizia-segnalata-${n.id}`} />
                {n._girata && <span data-testid={`notizia-girata-${n.id}`} className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-300">Girata a {n.owner_agent_name || "—"} · {n.stato_girata}</span>}
                {!n._masked && !(n.nome || n.cognome || n.proprietario?.nome || n.proprietario?.cognome) && (
                  <span data-testid={`da-completare-${n.id}`} className="inline-block mt-1 mr-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-300">Proprietario da individuare</span>
                )}
                {!n.tipologia && (
                  <span data-testid={`tipologia-da-completare-${n.id}`} className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">Tipologia da completare</span>
                )}
                <div className="text-xs text-[var(--muted)]">{[n.zona, tipoCatasto(n.tipologia, n.categoria_catastale)].filter(Boolean).join(" · ")}</div>
                {n.fonte && (
                  <div className="mt-1.5">
                    <span data-testid={`fonte-${n.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-[#FC82ED]/10 text-[#FC82ED] border border-[#FC82ED]/30">Lead · {FONTE_LABEL[n.fonte] || n.fonte}</span>
                    <span className="text-[10px] text-[var(--muted)] ml-2">Ricevuto {fmtDate(n.created_at)}</span>
                  </div>
                )}
              </div>
              <div className="flex items-start gap-1.5">
                {tempKey(n) === "senza" ? <span data-testid={`temp-senza-${n.id}`} className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border border-slate-300 bg-slate-50 text-slate-500 whitespace-nowrap">Senza temp.</span> : <TempBadge value={n.temperatura} />}
                {!n._masked && !n._girata && <AzioniNotizia notizia={n} compact onDone={load} />}
              </div>
            </div>
            <div className="text-sm text-slate-600 truncate">{n.via_immobile || n.immobile || n.indirizzo || (n.testo_notizia ? n.testo_notizia.split(" ").slice(0, 7).join(" ") + "…" : "")}</div>
            {n.provenienza && <div className="text-[11px] text-[var(--muted)]">Provenienza: <b className="text-slate-600">{n.provenienza}</b></div>}
            {n.valore_stimato > 0 && <div className="mono font-bold text-[#FC82ED]">{euro(n.valore_stimato)}</div>}
            {valutatoK(n.valutazione_immobilart) && <span data-testid={`valutato-${n.id}`} className="self-start text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#111111] text-white">{valutatoK(n.valutazione_immobilart)}</span>}

            {n._masked ? (
              <div className="flex items-center gap-2 text-xs text-[var(--muted)] bg-slate-50 rounded-lg px-3 py-2 border border-[var(--border)]">
                <Lock size={13} /> Dati riservati · Agente: <b className="text-slate-600">{n.agente_referente}</b>
              </div>
            ) : (
              <div className="flex flex-col gap-1 text-xs text-slate-500">
                {n.telefono && <a href={`tel:${String(n.telefono).replace(/[^\d+]/g, "")}`} onClick={(e) => e.stopPropagation()} data-testid={`notizia-tel-${n.id}`} className="flex items-center gap-1.5 hover:text-[#B8369F] underline-offset-2 hover:underline"><Phone size={12}/> {n.telefono}</a>}
                {n.email && <span className="flex items-center gap-1.5"><Mail size={12}/> {n.email}</span>}
                <MarketingBadge map={mktMap} id={n.id} />
              </div>
            )}
            {!storico && !n._masked && !n._girata && <div className="flex items-center justify-between gap-2 flex-wrap"><UltimoContattoBadge n={n} /><HoChiamato n={n} onDone={load} /></div>}

            <div className="mt-1 pt-3 border-t border-[var(--border)] flex flex-col gap-2.5">
              <div className="flex items-center justify-between gap-2">
                <StatusBadge value={n.stato} />
                {n.next_action && !n._masked && <span className="text-[11px] text-slate-500 truncate max-w-[55%] text-right">Prossima: <span className="text-[#111111] font-medium">{n.next_action}</span></span>}
              </div>
              {!n._masked && (
                n.stato === "incarico_acquisito" && n.immobile_id ? (
                  <>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span data-testid={`incarico-creato-${n.id}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide border text-emerald-500 bg-emerald-400/10 border-emerald-400/30">
                      <CheckCircle2 size={13} /> Trasformata in incarico
                    </span>
                    <button data-testid={`goto-incarico-${n.id}`} onClick={(e) => { e.stopPropagation(); nav(`/immobili/${n.immobile_id}`); }}
                            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-[#FC82ED] transition-colors">
                      Vai alla scheda incarico <ArrowRight size={13} />
                    </button>
                  </div>
                  {storico && convAt(n) && <div data-testid={`trasformata-il-${n.id}`} className="text-[10px] text-[var(--muted)]">Trasformata il {fmtDate(convAt(n))}</div>}
                  </>
                ) : (
                  <button data-testid={`convert-${n.id}`} onClick={(e) => { e.stopPropagation(); setConfirmN(n); }}
                          className="w-full py-2.5 rounded-xl bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm glow-pink transition-colors flex items-center justify-center gap-2">
                    <Sparkles size={15} /> Trasforma notizia in incarico
                  </button>
                )
              )}
            </div>
            </div>
          </div>
        ))}
      </div>);
      return carosello && caroselloEl ? createPortal(elencoNode, caroselloEl) : elencoNode; })()}

      {modal && <NotiziaModal onClose={() => setModal(false)} onSaved={() => { setModal(false); load(); }} />}
      {webhook && <WebhookModal onClose={() => setWebhook(false)} />}
      {showReport && <ReportChiamateModal onClose={() => setShowReport(false)} />}
      {confirmN && <ConvertiIncaricoModal n={confirmN} onClose={() => setConfirmN(null)} />}
    </div>
  );
}

function DupIndirizzoCheck({ via, comune }) {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!(via || "").trim()) { setList([]); return undefined; }
    const t = setTimeout(() => { api.get("/notizie-check-indirizzo", { params: { indirizzo: via, comune } }).then((r) => setList(r.data.duplicati || [])).catch(() => {}); }, 600);
    return () => clearTimeout(t);
  }, [via, comune]);
  return <DuplicatiIndirizzo list={list} />;
}

function NotiziaModal({ onClose, onSaved }) {
  const [f, setF] = useState({ temperatura: "media", tipologia: "", comune: "Reggio Emilia", stato: "nuova", probabilita: 50, provenienza: "", chiamata_entro: "" });
  const [civico, setCivico] = useState("");
  const [altri, setAltri] = useState(false);
  const [saving, setSaving] = useState(false);
  const { dup, setDup, check } = useDupCheck("notizia");
  const set = (k) => (e) => { const v = e.target.value; setF({ ...f, [k]: v }); if (["telefono", "email"].includes(k)) check(k, v); };
  const viaCompleta = [(f.via_immobile || "").trim(), civico.trim()].filter(Boolean).join(" ");

  const save = async () => {
    if (!(f.via_immobile || "").trim()) return toast.error("Inserisci la via");
    if (!(f.comune || "").trim()) return toast.error("Inserisci il comune");
    setSaving(true); setDup(null);
    try {
      const { data: nuova } = await api.post("/notizie", { ...f, via_immobile: viaCompleta, valore_stimato: Number(f.valore_stimato) || 0, probabilita: Number(f.probabilita) || 0 });
      if (f.gira_a) window.dispatchEvent(new Event("notizia-girata"));
      if (nuova?.contatto_presente) toast.warning(`Notizia creata. Contatto già presente: ${nuova.contatto_presente} – collegato in automatico`);
      else if (nuova?.contatto_collega) toast.warning("Notizia creata. Contatto già presente nel CRM come cliente di un collega: creato un tuo cliente venditore");
      else toast.success(f.gira_a ? "Notizia creata e girata" : "Notizia creata");
      onSaved();
    } catch (e) {
      const dd = dupDetail(e);
      if (dd) { setDup(dd); return; }
      toast.error(apiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Nuova Notizia" onClose={onClose} onSave={save} saving={saving} testid="notizia-modal">
      <DuplicateWarning dup={dup} />
      <Row><Field label="Via" required testid="f-via" value={f.via_immobile || ""} onChange={set("via_immobile")} /><Row><Field label="Civico" testid="f-civico" value={civico} onChange={(e) => setCivico(e.target.value)} /><Field label="Comune" required value={f.comune} testid="f-comune" onChange={set("comune")} /></Row></Row>
      <DupIndirizzoCheck via={viaCompleta} comune={f.comune} />
      <Row><Field label="Nome" testid="f-nome" onChange={set("nome")} /><Field label="Cognome" testid="f-cognome" onChange={set("cognome")} /></Row>
      <Row><Field label="Telefono" testid="f-telefono" onChange={set("telefono")} /><TipologiaSelect value={f.tipologia} required={false} testid="f-tipologia" onChange={(v) => setF({ ...f, tipologia: v })} /></Row>
      <TextArea label="Note" testid="f-note" value={f.note || ""} onChange={set("note")} rows={3} />
      <button type="button" data-testid="notizia-altri-dati" onClick={() => setAltri((a) => !a)} className="text-sm font-semibold text-[#FC82ED] hover:underline">{altri ? "− Nascondi altri dati" : "+ Altri dati"}</button>
      {altri && <>
      <Row><Field label="Email" testid="f-email" onChange={set("email")} /><Field label="Codice fiscale" testid="f-cf" onChange={set("codice_fiscale")} /></Row>
      <Row><Field label="Zona" testid="f-zona" onChange={set("zona")} />
        <Select label="Temperatura" value={f.temperatura} testid="f-temperatura" onChange={set("temperatura")} opts={[["calda","Calda"],["media","Media"],["fredda","Fredda"]]} />
      </Row>
      <Row>
        <CatastoSelect value={f.categoria_catastale} testid="f-catasto" onChange={(v) => setF({ ...f, categoria_catastale: v })} />
        <Select label="Provenienza" value={f.provenienza} testid="f-provenienza" onChange={set("provenienza")} opts={[["","— Seleziona —"], ...PROVENIENZE.map((p) => [p, p])]} />
      </Row>
      <Row>
        <Field label="Valore stimato €" type="euro" testid="f-valore" value={f.valore_stimato ?? ""} onChange={set("valore_stimato")} />
        <div />
      </Row>
      <TextArea label="Descrizione immobile" testid="f-descrizione" value={f.descrizione_immobile || ""} onChange={set("descrizione_immobile")} />
      <div className="rounded-xl border border-[#F43F5E]/30 bg-[#F43F5E]/[0.05] p-3">
        <div className="text-[11px] font-bold uppercase tracking-wide text-[#F43F5E] mb-2">Prossima attività · Chiamare entro il (facoltativo)</div>
        <Field label="Data della chiamata" type="date" testid="f-chiamata-entro" value={f.chiamata_entro} onChange={set("chiamata_entro")} />
      </div>
      <TextArea label="Prossima attività (nota)" testid="f-next" onChange={set("next_action")} />
      <GiraAField value={f.gira_a} onChange={(v) => setF({ ...f, gira_a: v })} note={f.note_girata} onNote={(v) => setF({ ...f, note_girata: v })} />
      </>}
    </Modal>
  );
}

function WebhookModal({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [copied, setCopied] = useState("");
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  useEffect(() => { api.get("/webhooks/config").then((r) => setCfg(r.data)); }, []);
  const copy = (txt, key) => { navigator.clipboard.writeText(txt); setCopied(key); setTimeout(() => setCopied(""), 1500); };
  return (
    <Modal title="Integrazione Lead Automatici" onClose={onClose} onSave={onClose} saveLabel="Chiudi" testid="webhook-modal">
      {!cfg ? <div className="skeleton h-40 rounded-xl" /> : (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Ricevi i lead da <b>Sito</b>, <b>Portali</b> e <b>Meta</b> con una richiesta <b>POST</b>. Includi l'header <code className="px-1 rounded bg-slate-100">{cfg.header}</code> con il segreto qui sotto. Ogni lead diventa una Notizia assegnata automaticamente all'agente meno carico.</p>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)]">Segreto Webhook</label>
            <div className="flex items-center gap-2 mt-1">
              <code data-testid="webhook-secret" className="flex-1 text-xs bg-slate-50 border border-[var(--border)] rounded-lg px-3 py-2 truncate">{cfg.secret}</code>
              <button data-testid="copy-secret" onClick={() => copy(cfg.secret, "secret")} className="p-2 rounded-lg border border-[var(--border)] hover:border-[#FC82ED]/40">{copied === "secret" ? <Check size={15} className="text-emerald-500" /> : <Copy size={15} />}</button>
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)]">Endpoint per fonte</label>
            {cfg.sources.map((s) => {
              const url = `${BACKEND}/api/webhooks/leads/${s.key}`;
              return (
                <div key={s.key} className="flex items-center gap-2">
                  <span className="text-xs font-semibold w-24 shrink-0">{s.label}</span>
                  <code className="flex-1 text-[11px] bg-slate-50 border border-[var(--border)] rounded-lg px-2 py-1.5 truncate">{url}</code>
                  <button data-testid={`copy-url-${s.key}`} onClick={() => copy(url, s.key)} className="p-1.5 rounded-lg border border-[var(--border)] hover:border-[#FC82ED]/40 shrink-0">{copied === s.key ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}</button>
                </div>
              );
            })}
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)]">Esempio payload JSON</label>
            <pre data-testid="webhook-sample" className="mt-1 text-[11px] bg-slate-50 border border-[var(--border)] text-slate-700 rounded-lg p-3 overflow-x-auto">{JSON.stringify(cfg.sample, null, 2)}</pre>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ---- shared modal primitives (exported for reuse) ---- */
const PILA_MODALI = [];

export function Modal({ title, children, onClose, onSave, saving, testid, saveLabel = "Salva", hideCancel = false, summary, scheda = false, value, onRestore, ultimoId, largo = false, header, footerLeft }) {
  const base = useRef(null);
  const touched = useRef(false);
  const alive = useRef(true);
  const [, rerender] = useState(0);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const cur = scheda ? JSON.stringify(value ?? null) : "";
  if (scheda && !touched.current) base.current = cur;
  const dirty = scheda && touched.current && cur !== base.current;
  const touch = () => { if (scheda && !touched.current) { touched.current = true; rerender((x) => x + 1); } };
  const doSave = async () => {
    try { await onSave(); } catch { return false; }
    await new Promise((r) => setTimeout(r, 0));
    if (!alive.current) { if (scheda) salvatoOk(); return true; }
    return false;
  };
  const { ask, dialog } = useUnsavedGuard(dirty, doSave);
  const chiudi = () => (dirty ? ask(onClose) : onClose());
  const annulla = () => {
    if (!onRestore) return onClose();
    onRestore(JSON.parse(base.current)); touched.current = false; rerender((x) => x + 1);
  };
  const pila = useRef(Symbol("modal"));
  useEffect(() => { const me = pila.current; PILA_MODALI.push(me); return () => { const i = PILA_MODALI.indexOf(me); if (i >= 0) PILA_MODALI.splice(i, 1); }; }, []);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") { if (PILA_MODALI[PILA_MODALI.length - 1] !== pila.current) return; if (document.querySelector("[data-radix-popper-content-wrapper]") || document.querySelector("[data-testid=unsaved-dialog]") || document.querySelector("[data-persona-open]")) return; e.stopPropagation(); chiudi(); } };
    document.addEventListener("keydown", h, true);
    return () => document.removeEventListener("keydown", h, true);
  });
  return createPortal(
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${largo ? "p-0 sm:p-4" : "p-4"}`} data-testid={testid}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={chiudi} />
      <div className={largo ? "relative w-full sm:max-w-[960px] h-[100dvh] sm:h-auto sm:max-h-[calc(100dvh-2rem)] flex flex-col bg-[#F7F7F8] sm:rounded-3xl shadow-[0_24px_64px_rgba(43,43,43,0.22)] overflow-hidden scheda-apri" : "relative w-full max-w-lg max-h-[calc(100dvh-2rem)] flex flex-col card-immo overflow-hidden"}>
        <div className={`flex items-start justify-between gap-3 border-b border-[var(--border)] shrink-0 ${largo ? "px-4 sm:px-7 pt-5 pb-3 bg-white" : "px-6 pt-6 pb-4"}`}>
          <div className="min-w-0 flex-1">{header || <h3 className="font-display text-lg font-bold">{title}</h3>}{ultimoId && <UltimoSalvataggio id={ultimoId} className={`text-[var(--muted)] mt-0.5 ${largo ? "text-[11px] mt-2" : ""}`} />}</div>
          <div className="flex items-center gap-2 shrink-0">
          <button onClick={chiudi} data-testid="modal-close" className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18} /></button></div>
        </div>
        <div className={`space-y-3 flex-1 overflow-y-auto overscroll-contain ${largo ? "px-4 sm:px-7 py-5" : "px-6 py-4"}`} onInputCapture={touch} onChangeCapture={touch} onPointerDownCapture={touch} onKeyDownCapture={touch}>
          {summary && <div data-testid="form-error-summary" className="rounded-lg border border-red-300 bg-red-50 text-red-700 text-sm font-medium px-3 py-2.5">{summary}</div>}
          {children}
        </div>
        {scheda ? (
          <div data-testid="modal-unsaved-bar" className="flex gap-2 px-4 sm:px-6 py-3 border-t border-[var(--border)] shrink-0 bg-[var(--card)] pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {footerLeft}
            {dirty
              ? <UnsavedActions saving={saving} error={summary} onSave={doSave} onCancel={annulla} saveLabel={saveLabel} />
              : <><span className="flex-1 self-center text-sm text-[var(--muted)]" data-testid="modal-no-changes">Nessuna modifica</span>
                  <button onClick={onClose} data-testid="modal-chiudi" className="px-5 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium hover:bg-slate-100">Chiudi</button></>}
          </div>
        ) : (
        <div className="flex gap-2 px-6 py-4 border-t border-[var(--border)] shrink-0 bg-[var(--card)]">
          {!hideCancel && <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium hover:bg-slate-100">Annulla</button>}
          <button onClick={onSave} disabled={saving} data-testid="modal-save"
                  className="flex-1 py-2.5 rounded-xl bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm glow-pink disabled:opacity-60">{saveLabel}</button>
        </div>
        )}
      </div>
      {dialog}
    </div>,
    document.body
  );
}
export const Row = ({ children }) => <div className="grid grid-cols-2 gap-3">{children}</div>;
const _fieldLabel = "block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5";
const _reqMark = (required) => required ? <span className="text-red-600 ml-0.5" aria-hidden>*</span> : null;
const _errMsg = (testid, error) => error ? <p data-testid={testid ? `${testid}-error` : undefined} className="mt-1 text-xs text-red-600 font-medium">{error}</p> : null;
const _ctrlCls = (error, disabled, extra = "") => "w-full px-3 py-2.5 rounded-lg bg-white border outline-none text-sm transition-colors "
  + (error ? "border-red-500 focus:border-red-500 ring-1 ring-red-500/30 " : "border-[var(--border)] focus:border-[#FC82ED] ")
  + (disabled ? "opacity-60 cursor-not-allowed bg-slate-50 " : "") + extra;

export function Field({ label, testid, type = "text", value, onChange, disabled = false, error, required }) {
  const inputCls = _ctrlCls(error, disabled);
  return (
    <div data-field-error={error ? "true" : undefined} data-field-name={testid}>
      <label className={_fieldLabel}>{label}{_reqMark(required)}</label>
      {type === "euro"
        ? <EuroInput testid={testid} value={value} onChange={onChange} disabled={disabled} className={inputCls} />
        : type === "password"
        ? <PasswordInput testid={testid} value={value} onChange={onChange} className={inputCls} />
        : <input data-testid={testid} type={type} value={value} onChange={onChange} disabled={disabled} className={inputCls} />}
      {_errMsg(testid, error)}
    </div>
  );
}
export function Select({ label, testid, value, onChange, opts, disabled = false, error, required }) {
  return (
    <div data-field-error={error ? "true" : undefined} data-field-name={testid}>
      <label className={_fieldLabel}>{label}{_reqMark(required)}</label>
      <select data-testid={testid} value={value} onChange={onChange} disabled={disabled} className={_ctrlCls(error, disabled)}>
        {opts.map((o) => Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>)}
      </select>
      {_errMsg(testid, error)}
    </div>
  );
}
export function TextArea({ label, testid, value, onChange, rows = 4, error, required }) {
  return (
    <div data-field-error={error ? "true" : undefined} data-field-name={testid}>
      <label className={_fieldLabel}>{label}{_reqMark(required)}</label>
      <LongTextarea data-testid={testid} rows={rows} value={value} onChange={onChange} className={_ctrlCls(error, false)} />
      {_errMsg(testid, error)}
    </div>
  );
}
function Empty({ storico }) {
  return <div className="card-immo p-12 text-center"><Flame className="mx-auto text-[var(--muted)] mb-3" size={32} /><p className="text-[var(--muted)] text-sm">{storico ? "Nessuna notizia trasformata in incarico." : "Nessuna notizia. Crea la prima opportunità di acquisizione."}</p></div>;
}

function ReportChiamateModal({ onClose }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/report/chiamate?days=${days}`).then((r) => setData(r.data))
      .catch(() => setData({ per_agente: [], totali: { completate: 0, rimandate: 0 } }));
  }, [days]);
  return (
    <Modal title={`Report chiamate · ultimi ${days} giorni`} onClose={onClose} onSave={onClose} saveLabel="Chiudi" testid="report-chiamate-modal">
      <div className="flex gap-1 bg-[#F7F7F8] border border-[var(--border)] rounded-xl p-1 mb-3 w-max">
        {[[7,"7 giorni"],[30,"30 giorni"]].map(([v,l]) => (
          <button key={v} data-testid={`report-days-${v}`} onClick={() => setDays(v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${days===v ? "bg-[#FC82ED] text-black" : "text-slate-600 hover:text-[#111111]"}`}>{l}</button>
        ))}
      </div>
      {!data ? <div className="skeleton h-40 rounded-xl" /> : (
        <div className="space-y-3">
          <div className="flex gap-3">
            <div className="flex-1 rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-3"><div className="text-[11px] uppercase font-bold text-emerald-600">Completate</div><div data-testid="report-tot-completate" className="mono text-2xl font-extrabold text-emerald-600">{data.totali.completate}</div></div>
            <div className="flex-1 rounded-xl border border-[#FC82ED]/30 bg-[#FC82ED]/10 p-3"><div className="text-[11px] uppercase font-bold text-[#FC82ED]">Rimandate</div><div data-testid="report-tot-rimandate" className="mono text-2xl font-extrabold text-[#FC82ED]">{data.totali.rimandate}</div></div>
          </div>
          {data.per_agente.length === 0 ? <div className="text-sm text-[var(--muted)] text-center py-6">Nessuna attività di chiamata nel periodo.</div> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase text-[var(--muted)] border-b border-[var(--border)]"><th className="py-2">Agente</th><th className="py-2 text-center">Completate</th><th className="py-2 text-center">Rimandate</th><th className="py-2 text-center">Totale</th></tr></thead>
              <tbody>
                {data.per_agente.map((a) => (
                  <tr key={a.agent_id} data-testid={`report-row-${a.agent_id}`} className="border-b border-[var(--border)] last:border-0">
                    <td className="py-2 font-medium">{a.nome || "—"}</td>
                    <td className="py-2 text-center mono text-emerald-600 font-semibold">{a.completate}</td>
                    <td className="py-2 text-center mono text-[#FC82ED] font-semibold">{a.rimandate}</td>
                    <td className="py-2 text-center mono font-bold">{a.completate + a.rimandate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Modal>
  );
}
